<template>
  <div>
    <div v-if="!panelOpen" class="cfloat">
      <div class="cbubble" role="button" tabindex="0" aria-label="打开 AI 问答助手" @click="openPanel">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-8.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"/>
          <line x1="9" y1="10.5" x2="9.01" y2="10.5"/>
          <line x1="12" y1="10.5" x2="12.01" y2="10.5"/>
          <line x1="15" y1="10.5" x2="15.01" y2="10.5"/>
        </svg>
      </div>
    </div>
    <div v-show="panelOpen" class="cpanel" :class="{ flash: flashing }">
      <div class="cp-head">
        <span class="cp-logo">F</span>
        <span class="cp-title">AI 问答助手</span>
        <span class="head-actions">
          <button type="button" class="cp-x" aria-label="关闭面板" @click="closePanel">×</button>
        </span>
      </div>
      <div v-if="embedMode" class="cp-tabs">
        <button :class="{ active: tab === 'chat' }" @click="tab = 'chat'">对话</button>
        <button :class="{ active: tab === 'search' }" @click="tab = 'search'">检索</button>
      </div>
      <div class="cp-main">
        <chat-conversation v-show="tab === 'chat'" ref="conv" variant="float" />
        <div v-show="tab === 'search'" class="cp-search">
          <div class="search-row">
            <el-input
              v-model="searchQ"
              placeholder="关键词检索知识库..."
              clearable
              @keyup.enter.native="doSearch"
            />
            <el-button type="primary" size="small" :loading="searching" @click="doSearch">检索</el-button>
          </div>
          <div v-for="k in searchResults" :key="k.documentId" class="doc-card" @click="openDoc(k)">
            <div class="thumb">文档</div>
            <div>
              <div class="dt">{{ k.title }} <span v-if="k._matchType === 'semantic'" class="badge admin">语义命中</span><span v-else-if="k._matchType === 'keyword'" class="badge pub">关键词命中</span></div>
              <div class="dm">{{ excerpt(k.content) }}...</div>
            </div>
          </div>
          <p v-if="searched && !searchResults.length" class="muted">未在知识库中找到相关内容</p>
        </div>
      </div>
    </div>
    <doc-modal :show="docShow" :loading="docLoading" :doc="currentDoc" @close="docShow = false" />
  </div>
</template>

<script>
import { chatApi, knowledgeApi, mapSources, formatExcerpt } from '@/api'
import ChatConversation from './ChatConversation.vue'
import DocModal from './DocModal.vue'

export default {
  name: 'ChatFloat',
  components: { ChatConversation, DocModal },
  props: {
    embedMode: { type: Boolean, default: false }
  },
  data() {
    return {
      panelOpen: false,
      flashing: false,
      tab: 'chat',
      searchQ: '',
      searching: false,
      searched: false,
      searchResults: [],
      docShow: false,
      docLoading: false,
      currentDoc: {}
    }
  },
  created() {
    this.$root.$on('open-chat', this.onExternalOpen)
    this.$root.$on('ask-in-float', this.onExternalAsk)
  },
  beforeDestroy() {
    this.$root.$off('open-chat', this.onExternalOpen)
    this.$root.$off('ask-in-float', this.onExternalAsk)
  },
  methods: {
    excerpt: formatExcerpt,
    openPanel() {
      this.panelOpen = true
    },
    closePanel() {
      this.panelOpen = false
    },
    onExternalOpen() {
      this.tab = 'chat'
      this.openPanel()
      this.flash()
    },
    onExternalAsk(q) {
      this.tab = 'chat'
      this.openPanel()
      this.flash()
      this.$nextTick(() => {
        if (this.$refs.conv) this.$refs.conv.ask(q)
      })
    },
    flash() {
      this.flashing = true
      setTimeout(() => { this.flashing = false }, 1200)
    },
    async doSearch() {
      const q = this.searchQ.trim()
      if (!q) {
        this.$message.warning('请输入检索关键词')
        return
      }
      this.searching = true
      this.searched = true
      try {
        const res = await chatApi.askPlain(q)
        this.searchResults = mapSources((res.data && res.data.sources) || [])
      } catch (e) {
        this.searchResults = []
        this.$message.error(e.message || '检索失败，请稍后重试')
      } finally {
        this.searching = false
      }
    },
    async openDoc(k) {
      const documentId = k.documentId
      if (!documentId) return
      this.docShow = true
      this.docLoading = true
      this.currentDoc = { title: k.title || '加载中...', content: '' }
      try {
        const res = await knowledgeApi.getByDocumentId(documentId)
        this.currentDoc = res.data
      } catch (e) {
        this.$message.error(e.message || '加载文章详情失败')
        this.docShow = false
      } finally {
        this.docLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cfloat {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2500;
}

.cbubble {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, $blue, $lblue);
  color: #fff;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(26, 115, 184, 0.45);
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid $lblue;
    animation: pulse 2.4s ease-out infinite;
  }

  &:hover::after { animation-play-state: paused; }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.7); opacity: 0; }
}

.cpanel {
  position: fixed;
  right: 24px;
  bottom: 82px;
  width: 372px;
  height: 540px;
  background: $surface;
  border: 1px solid $line;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(15, 41, 66, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2501;

  &.flash {
    animation: cpflash 1.1s ease;
  }
}

@keyframes cpflash {
  0% { box-shadow: 0 10px 40px rgba(15, 41, 66, 0.18); }
  35% { box-shadow: 0 0 0 3px $blue, 0 10px 40px rgba(15, 41, 66, 0.18); }
  100% { box-shadow: 0 10px 40px rgba(15, 41, 66, 0.18); }
}

.cp-head {
  background: linear-gradient(135deg, $navy, $lblue);
  color: #fff;
  padding: 11px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;

  .cp-logo {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
  }

  .cp-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .head-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .cp-x {
    background: none;
    border: none;
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    line-height: 1;

    &:hover { opacity: 0.85; }
  }
}

.cp-tabs {
  display: flex;
  border-bottom: 1px solid $line;
  background: $surface;
  flex-shrink: 0;

  button {
    flex: 1;
    padding: 10px;
    background: transparent;
    border: none;
    color: $grey;
    font-size: 13px;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;

    &:hover { color: $blue; }

    &.active {
      color: $blue;
      border-bottom-color: $blue;
      font-weight: 600;
    }
  }
}

.cp-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.cp-search {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px;
  background: $bg;
}

.search-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

@media (max-width: 640px) {
  .cfloat { right: 14px; bottom: 14px; }

  .cpanel {
    right: 8px;
    left: 8px;
    bottom: 72px;
    width: auto;
    height: 70vh;
  }
}
</style>
